import type { LayerSpec } from '@/core/pipeline';
import { parseHex, type RGB } from '@/lib/color';

type LatticeParams = {
  shape: 'square' | 'checker' | 'hex' | 'triangle' | 'lines';
  size: number;
  a: string;
  b: string;
  thickness: number;
};

export const latticeSpec: LayerSpec = {
  kind: 'lattice',
  category: 'generator',
  animated: false,
  label: 'Lattice',
  defaults: {
    shape: 'checker',
    size: 16,
    a: '#0a0a0a',
    b: '#ededed',
    thickness: 1,
  } satisfies LatticeParams,
  schema: {
    shape: {
      type: 'select',
      label: 'Shape',
      options: ['square', 'checker', 'hex', 'triangle', 'lines'],
      default: 'checker',
    },
    size:      { type: 'int',    label: 'Size',      min: 2, max: 128, step: 1, default: 16 },
    thickness: { type: 'int',    label: 'Thickness', min: 1, max: 16,  step: 1, default: 1  },
    a:         { type: 'color',  label: 'Color A',   default: '#0a0a0a' },
    b:         { type: 'color',  label: 'Color B',   default: '#ededed' },
  },
  run: (_input, params, ctx) => {
    const p = params as LatticeParams;
    const colA = parseHex(p.a) ?? ([0, 0, 0] as RGB);
    const colB = parseHex(p.b) ?? ([255, 255, 255] as RGB);
    const w = ctx.width, h = ctx.height;
    const out = new ImageData(w, h);
    const data = out.data;
    const size = Math.max(2, Math.floor(p.size));
    const t = Math.max(1, Math.floor(p.thickness));

    let i = 0;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++, i += 4) {
        const useA = pick(p.shape, x, y, size, t);
        const c = useA ? colA : colB;
        data[i] = c[0]; data[i+1] = c[1]; data[i+2] = c[2]; data[i+3] = 255;
      }
    }
    return out;
  },
};

function pick(shape: string, x: number, y: number, s: number, t: number): boolean {
  switch (shape) {
    case 'square':
      // grid of A squares with B borders of thickness `t`
      return (x % s) >= t && (y % s) >= t;
    case 'checker':
      return ((Math.floor(x / s) + Math.floor(y / s)) & 1) === 0;
    case 'hex': {
      // Approximate flat-top hex grid via offset rows
      const rowH = Math.max(1, Math.floor(s * 0.866));
      const row = Math.floor(y / rowH);
      const xOff = (row & 1) === 1 ? s / 2 : 0;
      const cx = Math.floor((x + xOff) / s);
      return (((cx + row) & 1) === 0);
    }
    case 'triangle': {
      const xs = x % s;
      const ys = y % s;
      const upper = (xs + ys) < s;
      return upper;
    }
    case 'lines':
      return (y % s) >= t;
    default:
      return true;
  }
}
